import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { UI, DISPLAY } from '../styles/tl5';
import { ROUTES } from '../constants/routes';
import { useCreateTicketMutation } from '../store/supportApi';
import { useGetContactInfoQuery } from '../store/siteContentApi';

// Doctor Consult — "Talk to a lab doctor", built to the v5 reference.
//
// There is no consultation endpoint, no doctor roster and no slot
// availability anywhere in the API. Three consequences, all of them
// visible in the copy:
//
//   · The request is filed as a real support ticket
//     (POST /support/tickets, which takes guests and attachments), so it
//     reaches staff and the patient can follow it at /my-tickets. The
//     nearest issue category is "Help me understand my report", which is
//     about an existing report rather than choosing tests, so it goes in
//     as "Something Else" with everything spelled out in the subject.
//     A consultation category would be better. API-NOTES 2.35.
//   · The times are offered as a **preferred** slot, not a booked one.
//     The reference's done state promises "Dr. [Name] will call you on
//     98765 43210 at Today, 11:00 am" — we have no scheduling system
//     behind that, and committing a doctor to a named minute we cannot
//     honour is not a promise worth making. Ours confirms on WhatsApp.
//   · The consultation fee is `₹ [fee]` in the reference and is not in
//     any endpoint. The card keeps its shape and says the fee is
//     confirmed when we call. API-NOTES 2.36.

// `mid` is the mid-sentence form: lower-casing the title gives
// "your whatsapp chat", which is not how the brand is written.
const MODES = [
  { key: 'phone', t: 'Phone call', d: 'The doctor calls you', mid: 'phone call' },
  { key: 'video', t: 'Video call', d: 'Link sent on WhatsApp', mid: 'video call' },
  { key: 'whatsapp', t: 'WhatsApp chat', d: 'Chat with a doctor', mid: 'WhatsApp chat' },
];

const TOPICS = [
  'Unexplained symptoms', 'Diabetes', 'Thyroid', 'Heart and cholesterol',
  'Women’s health', 'Pregnancy planning', 'Parents over 60',
  'Understanding an old report', 'Choosing a package', 'Family history',
];

const SLOTS = ['8:00 am', '9:30 am', '11:00 am', '12:30 pm', '3:00 pm', '4:30 pm', '6:00 pm', '7:30 pm'];

const DAYS = ['Today', 'Tomorrow'];

const STEPS = [
  'Book a slot, today or tomorrow',
  'A pathologist calls you',
  'Recommended tests sent to WhatsApp, ready to book',
];

const PROMISES = ['15-minute consultation', 'Call, video or WhatsApp', 'Fee adjusted when you book tests'];

const INFO = [
  ['Who you’ll speak to', 'A qualified TrustLab pathologist or lab physician who reads reports every day.'],
  ['Good for', 'Unexplained symptoms, family history, follow-up after a past report, or choosing between packages.'],
  ['Not a replacement', 'We help you choose tests. Diagnosis and treatment stay with your own doctor.'],
];

const MAX_FILES = 5;
const MAX_BYTES = 5 * 1024 * 1024;

// ── shared fragments ──────────────────────────────────────────────────
const panel = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
  p: 'clamp(20px, 3.4vw, 32px)',
  borderRadius: '28px',
  bgcolor: '#FFFFFF',
};

const sectionTitle = {
  m: 0,
  fontFamily: DISPLAY,
  fontWeight: 700,
  fontSize: 'clamp(26px, 3.2vw, 36px)',
  lineHeight: 1.05,
  letterSpacing: '-.02em',
  color: 'var(--tl-g9)',
};

const field = {
  border: '1.5px solid var(--tl-line)',
  borderRadius: '14px',
  outline: 0,
  font: 'inherit',
  fontFamily: UI,
  fontSize: 16,
  p: '13px 14px',
  color: 'var(--tl-g9)',
  bgcolor: '#FFFFFF',
  width: '100%',
  minWidth: 0,
  '&:focus': { borderColor: 'var(--tl-teal)' },
};

const fieldLabel = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
  minWidth: 0,
  fontFamily: UI,
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--tl-muted-2)',
};

const DoctorConsult = () => {
  const [createTicket, { isLoading: sending }] = useCreateTicketMutation();
  const { data: contact } = useGetContactInfoQuery();

  const [mode, setMode] = useState(0);
  const [topics, setTopics] = useState([]);
  const [notes, setNotes] = useState('');
  const [day, setDay] = useState(0);
  const [slot, setSlot] = useState(2);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [files, setFiles] = useState([]);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null);
  const fileInput = useRef(null);

  const whatsapp = String(contact?.whatsapp || '+919014638633').replace(/\D/g, '');

  const when = `${DAYS[day]}, ${SLOTS[slot]}`;
  const ok = name.trim() && phone.replace(/\D/g, '').length >= 10;

  const topicText = topics.length
    ? topics.slice(0, 2).join(', ') + (topics.length > 2 ? ` +${topics.length - 2}` : '')
    : 'Not chosen';

  const toggleTopic = (t) =>
    setTopics((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));

  const pickFiles = (list) => {
    const picked = Array.from(list || []);
    if (!picked.length) return;
    const tooBig = picked.find((f) => f.size > MAX_BYTES);
    if (tooBig) {
      setError(`${tooBig.name} is over 5 MB. Please attach a smaller file.`);
      return;
    }
    setError('');
    setFiles((cur) => [...cur, ...picked].slice(0, MAX_FILES));
  };

  const summary = useMemo(() => [
    ['Mode', MODES[mode].t],
    ['When', when],
    ['Topics', topicText],
  ], [mode, when, topicText]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return setError('Please enter your name');
    if (phone.replace(/\D/g, '').length < 10) return setError('Please enter a 10-digit mobile number');
    setError('');

    const message = [
      `Preferred mode: ${MODES[mode].t}`,
      `Preferred time: ${when}`,
      `Topics: ${topics.length ? topics.join(', ') : 'not chosen'}`,
      notes.trim() ? `Notes: ${notes.trim()}` : null,
    ].filter(Boolean).join('\n');

    try {
      await createTicket({
        name: name.trim(),
        phone: phone.trim(),
        category: 'other',
        priority: 'normal',
        subject: `Doctor consultation request — ${MODES[mode].t}, ${when}`,
        message,
        attachments: files,
      }).unwrap();
      setSent({ first: name.trim().split(' ')[0] || 'there', when, mode: MODES[mode].mid, phone: phone.trim() });
    } catch (err) {
      setError(err?.data?.message || 'Could not send your request — please try again.');
    }
    return undefined;
  };

  return (
    <Box
      component="main"
      sx={{ width: '100%', maxWidth: 1200, mx: 'auto', px: 2, pt: 2, pb: 9, display: 'flex', flexDirection: 'column', gap: 1.5 }}
    >
      {/* ── 1. hero ──────────────────────────────────────────────────── */}
      <Box component="section" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: 1.5 }}>
        <Box
          sx={{
            flex: '1.4 1 480px',
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2.25,
            p: 'clamp(26px, 4.5vw, 52px)',
            borderRadius: '32px',
            bgcolor: 'var(--tl-g9)',
            color: '#FFFFFF',
          }}
        >
          <Box
            component="span"
            sx={{
              alignSelf: 'start',
              p: '6px 10px',
              borderRadius: '999px',
              bgcolor: 'var(--tl-yellow)',
              color: 'var(--tl-g9)',
              fontFamily: UI,
              fontSize: 12,
              fontWeight: 800,
            }}
          >
            Talk to a lab doctor
          </Box>
          <Typography
            component="h1"
            sx={{
              m: 0,
              fontFamily: DISPLAY,
              fontWeight: 700,
              fontSize: 'clamp(32px, 5vw, 64px)',
              lineHeight: 0.98,
              letterSpacing: '-.035em',
              textWrap: 'balance',
              color: '#FFFFFF',
            }}
          >
            Not sure which test you need?
          </Typography>
          <Typography sx={{ m: 0, fontFamily: UI, fontSize: 17, lineHeight: 1.6, color: '#C7DDD7', maxWidth: 520 }}>
            Speak to a TrustLab doctor before you book. Tell us your symptoms, history or what
            your doctor said, and we&rsquo;ll recommend only the tests that matter.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2.25, fontFamily: UI, fontSize: 14, fontWeight: 700, color: '#E4F1ED' }}>
            {PROMISES.map((p) => (
              <Box key={p} component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box component="span" aria-hidden="true" sx={{ color: 'var(--tl-teal-l)' }}>✓</Box>
                {p}
              </Box>
            ))}
          </Box>
        </Box>

        <Box sx={{ ...panel, flex: '1 1 320px', justifyContent: 'center' }}>
          <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, fontSize: 15, color: 'var(--tl-muted-2)' }}>
            Consultation fee
          </Box>
          {/* The reference prints `₹ [fee]`. No endpoint carries one, and a
              number invented here is a number someone gets charged.
              API-NOTES 2.36. */}
          <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 30, lineHeight: 1.1, color: 'var(--tl-g9)' }}>
            Confirmed when we call
          </Box>
          <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.55, color: 'var(--tl-muted)' }}>
            Fully adjusted against any tests you book within 7 days.
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1.75, borderTop: '1px solid #EEF3F1', fontFamily: UI, fontSize: 14 }}>
            {STEPS.map((s, i) => (
              <Box key={s} component="span" sx={{ display: 'flex', gap: 1.25, color: 'var(--tl-g9)' }}>
                <Box component="span" sx={{ color: 'var(--tl-teal)', fontWeight: 800 }}>{i + 1}</Box>
                {s}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {sent ? (
        /* ── done ─────────────────────────────────────────────────── */
        <Box component="section" sx={{ ...panel, maxWidth: 680, width: '100%', mx: 'auto', mt: 1.5 }}>
          <Box
            component="span"
            aria-hidden="true"
            sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: 'var(--tl-teal)', color: '#FFFFFF', display: 'grid', placeItems: 'center', fontSize: 26, fontWeight: 800 }}
          >
            ✓
          </Box>
          <Box component="span" sx={sectionTitle}>Request sent, {sent.first}.</Box>
          <Box component="span" sx={{ fontFamily: UI, fontSize: 16, lineHeight: 1.6, color: 'var(--tl-muted)' }}>
            We&rsquo;ll message {sent.phone} on WhatsApp to confirm your {sent.mode} for{' '}
            {sent.when}. Keep any old reports handy.
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: '16px 18px', borderRadius: '18px', bgcolor: '#F6FAF8', fontFamily: UI, fontSize: 14, lineHeight: 1.55, color: '#2E4540' }}>
            <Box component="span" sx={{ fontWeight: 800, color: 'var(--tl-g9)' }}>After your call</Box>
            <Box component="span">
              The doctor&rsquo;s recommended tests arrive on WhatsApp as a ready-to-book package.
              The consultation fee is adjusted when you book within 7 days.
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Box component={Link} to={ROUTES.HOME} sx={{ borderRadius: '16px', p: '15px 22px', bgcolor: 'var(--tl-g9)', color: '#FFFFFF', fontFamily: UI, fontSize: 16, fontWeight: 800, textDecoration: 'none' }}>
              Back to home
            </Box>
            <Box component={Link} to={ROUTES.MY_TICKETS} sx={{ borderRadius: '16px', p: '15px 22px', bgcolor: 'var(--tl-mint)', color: 'var(--tl-g9)', fontFamily: UI, fontSize: 16, fontWeight: 800, textDecoration: 'none' }}>
              Track this request
            </Box>
          </Box>
        </Box>
      ) : (
        /* ── the form ─────────────────────────────────────────────── */
        <Box component="form" onSubmit={onSubmit} noValidate sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 1.5 }}>
          <Box sx={{ flex: '2 1 560px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* how would you like to talk */}
            <Box component="section" sx={panel}>
              <Box component="span" sx={sectionTitle}>How would you like to talk?</Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))', gap: 1 }}>
                {MODES.map((m, i) => {
                  const on = i === mode;
                  return (
                    <Box
                      key={m.key}
                      component="button"
                      type="button"
                      onClick={() => setMode(i)}
                      aria-pressed={on}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        textAlign: 'left',
                        cursor: 'pointer',
                        p: 2,
                        borderRadius: '18px',
                        border: `2px solid ${on ? 'var(--tl-teal)' : 'var(--tl-line)'}`,
                        bgcolor: on ? 'var(--tl-mint)' : '#FFFFFF',
                        color: 'var(--tl-g9)',
                        font: 'inherit',
                      }}
                    >
                      <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, fontSize: 16 }}>{m.t}</Box>
                      <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted-2)' }}>{m.d}</Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* what to discuss */}
            <Box component="section" sx={panel}>
              <Box component="span" sx={sectionTitle}>What would you like to discuss?</Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {TOPICS.map((t) => {
                  const on = topics.includes(t);
                  return (
                    <Box
                      key={t}
                      component="button"
                      type="button"
                      onClick={() => toggleTopic(t)}
                      aria-pressed={on}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: '999px',
                        p: '10px 14px',
                        font: 'inherit',
                        fontFamily: UI,
                        fontSize: 14,
                        fontWeight: 800,
                        border: `1.5px solid ${on ? 'var(--tl-teal)' : 'var(--tl-line)'}`,
                        bgcolor: on ? 'var(--tl-teal)' : '#FFFFFF',
                        color: on ? '#FFFFFF' : 'var(--tl-g9)',
                      }}
                    >
                      {on ? `${t} ✓` : t}
                    </Box>
                  );
                })}
              </Box>

              <Box component="label" sx={fieldLabel}>
                Anything else the doctor should know? (optional)
                <Box
                  component="textarea"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="e.g. tired for 3 months, on BP medicine, father had diabetes"
                  sx={{ ...field, resize: 'vertical', lineHeight: 1.6 }}
                />
              </Box>

              <Box
                component="label"
                htmlFor="consult-files"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); pickFiles(e.dataTransfer?.files); }}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  cursor: 'pointer',
                  p: '14px 16px',
                  borderRadius: '18px',
                  border: '2px dashed #B9DCD3',
                }}
              >
                <Box component="span" sx={{ fontFamily: UI, fontSize: 14, fontWeight: 700, color: 'var(--tl-g9)' }}>
                  {files.length
                    ? `${files.length} file${files.length > 1 ? 's' : ''} attached — ${files.map((f) => f.name).join(', ')}`
                    : 'Have old reports or a prescription? Share them for a better consultation.'}
                </Box>
                <Box
                  component="span"
                  sx={{ p: '9px 14px', borderRadius: '999px', bgcolor: 'var(--tl-mint)', fontFamily: UI, fontWeight: 800, fontSize: 13, color: 'var(--tl-g9)' }}
                >
                  {files.length ? 'Add more' : 'Upload'}
                </Box>
                <Box
                  component="input"
                  id="consult-files"
                  ref={fileInput}
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,image/*,application/pdf"
                  onChange={(e) => pickFiles(e.target.files)}
                  sx={{ position: 'absolute', width: '1px', height: '1px', p: 0, m: '-1px', overflow: 'hidden', clipPath: 'inset(50%)', border: 0 }}
                />
              </Box>
            </Box>

            {/* pick a time */}
            <Box component="section" sx={panel}>
              <Box component="span" sx={sectionTitle}>Pick a time</Box>
              <Box sx={{ display: 'flex', gap: 0.75 }}>
                {DAYS.map((d, i) => {
                  const on = i === day;
                  return (
                    <Box
                      key={d}
                      component="button"
                      type="button"
                      onClick={() => setDay(i)}
                      aria-pressed={on}
                      sx={{
                        border: 0,
                        cursor: 'pointer',
                        borderRadius: '999px',
                        p: '10px 18px',
                        font: 'inherit',
                        fontFamily: UI,
                        fontSize: 14,
                        fontWeight: 800,
                        bgcolor: on ? 'var(--tl-g9)' : 'var(--tl-mint)',
                        color: on ? '#FFFFFF' : 'var(--tl-g9)',
                      }}
                    >
                      {d}
                    </Box>
                  );
                })}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 120px), 1fr))', gap: 1 }}>
                {SLOTS.map((t, i) => {
                  const on = i === slot;
                  return (
                    <Box
                      key={t}
                      component="button"
                      type="button"
                      onClick={() => setSlot(i)}
                      aria-pressed={on}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: '14px',
                        p: '12px 8px',
                        font: 'inherit',
                        fontFamily: UI,
                        fontSize: 14,
                        fontWeight: 800,
                        border: `1.5px solid ${on ? 'var(--tl-teal)' : 'var(--tl-line)'}`,
                        bgcolor: on ? 'var(--tl-teal)' : '#FFFFFF',
                        color: on ? '#FFFFFF' : 'var(--tl-g9)',
                      }}
                    >
                      {t}
                    </Box>
                  );
                })}
              </Box>
              <Box component="span" sx={{ fontFamily: UI, fontSize: 13, color: 'var(--tl-muted-2)' }}>
                We confirm your slot on WhatsApp before the call.
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 1.25 }}>
                <Box component="label" sx={fieldLabel}>
                  Full name
                  <Box component="input" value={name} onChange={(e) => setName(e.target.value)} sx={field} />
                </Box>
                <Box component="label" sx={fieldLabel}>
                  Mobile (WhatsApp)
                  <Box component="input" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="+91" sx={field} />
                </Box>
              </Box>
            </Box>
          </Box>

          {/* ── summary ──────────────────────────────────────────── */}
          <Box
            component="aside"
            sx={{
              flex: '1 1 300px',
              minWidth: 0,
              position: 'sticky',
              top: 100,
              display: 'flex',
              flexDirection: 'column',
              gap: 1.75,
              p: 3,
              borderRadius: '28px',
              bgcolor: 'var(--tl-teal)',
              color: '#FFFFFF',
            }}
          >
            <Box component="span" sx={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 22, color: '#FFFFFF' }}>
              Your consultation
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, fontFamily: UI, fontSize: 14 }}>
              {summary.map(([k, v]) => (
                <Box key={k} component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.25 }}>
                  <Box component="span" sx={{ color: '#DFFBF4' }}>{k}</Box>
                  <Box component="span" sx={{ fontWeight: 800, textAlign: 'right' }}>{v}</Box>
                </Box>
              ))}
              <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.25, pt: 1.25, borderTop: '1px solid rgba(255,255,255,0.25)' }}>
                <Box component="span" sx={{ color: '#DFFBF4' }}>Fee</Box>
                <Box component="span" sx={{ fontWeight: 800, textAlign: 'right' }}>Confirmed on the call</Box>
              </Box>
            </Box>

            {error && (
              <Box component="span" role="alert" sx={{ fontFamily: UI, fontSize: 13, fontWeight: 700, color: '#FFF3C4' }}>
                {error}
              </Box>
            )}

            <Box
              component="button"
              type="submit"
              disabled={!ok || sending}
              sx={{
                border: 0,
                cursor: ok && !sending ? 'pointer' : 'default',
                borderRadius: '16px',
                p: '15px 22px',
                font: 'inherit',
                fontFamily: UI,
                fontSize: 16,
                fontWeight: 800,
                textAlign: 'center',
                bgcolor: ok && !sending ? 'var(--tl-g9)' : 'rgba(11,43,39,0.35)',
                color: '#FFFFFF',
              }}
            >
              {sending ? 'Sending…' : 'Request consultation'}
            </Box>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 12, lineHeight: 1.5, color: '#DFFBF4' }}>
              This is not an emergency service. For urgent symptoms, call 108 or visit the
              nearest hospital.
            </Box>
            <Box
              component="a"
              href={`https://wa.me/${whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontFamily: UI, fontSize: 13, fontWeight: 800, color: '#FFFFFF' }}
            >
              Or message us on WhatsApp →
            </Box>
          </Box>
        </Box>
      )}

      {/* ── 3. what this is ───────────────────────────────────────── */}
      <Box
        component="section"
        sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))', gap: 1.5, mt: 4.5 }}
      >
        {INFO.map(([t, d]) => (
          <Box key={t} sx={panel}>
            <Box component="span" sx={{ fontFamily: UI, fontWeight: 800, fontSize: 17, color: 'var(--tl-g9)' }}>{t}</Box>
            <Box component="span" sx={{ fontFamily: UI, fontSize: 15, lineHeight: 1.55, color: 'var(--tl-muted-2)' }}>{d}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default DoctorConsult;
